import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Plus, Trash2, Bell, BellOff, TrendingUp, TrendingDown, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const SYMBOLS = [
  { symbol: "BTC", name: "Bitcoin", type: "crypto" },
  { symbol: "ETH", name: "Ethereum", type: "crypto" },
  { symbol: "XRP", name: "Ripple", type: "crypto" },
  { symbol: "SOL", name: "Solana", type: "crypto" },
  { symbol: "ADA", name: "Cardano", type: "crypto" },
  { symbol: "DOGE", name: "Dogecoin", type: "crypto" },
  { symbol: "GOLD", name: "Gold", type: "metal" },
  { symbol: "SILVER", name: "Silver", type: "metal" },
];

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    symbol: "",
    condition: "above",
    target_price: "",
  });

  const fetchAlerts = useCallback(async () => {
    try {
      const [alertsRes, pricesRes] = await Promise.all([
        api.get("/alerts"),
        api.get("/prices")
      ]);
      setAlerts(alertsRes.data);
      setPrices(pricesRes.data.prices);
    } catch (error) {
      console.error("Error fetching alerts:", error);
      toast.error("Failed to load alerts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const handleCreateAlert = async (e) => {
    e.preventDefault();
    
    const selectedSymbol = SYMBOLS.find(s => s.symbol === formData.symbol);
    if (!selectedSymbol) {
      toast.error("Please select a symbol");
      return;
    }

    try {
      await api.post("/alerts", {
        symbol: selectedSymbol.symbol,
        asset_type: selectedSymbol.type,
        condition: formData.condition,
        target_price: parseFloat(formData.target_price),
      });
      
      toast.success("Alert created successfully");
      setDialogOpen(false);
      setFormData({ symbol: "", condition: "above", target_price: "" });
      fetchAlerts();
    } catch (error) {
      console.error("Error creating alert:", error);
      toast.error("Failed to create alert");
    }
  };

  const handleToggleAlert = async (alertId) => {
    try {
      await api.put(`/alerts/${alertId}/toggle`);
      toast.success("Alert status updated");
      fetchAlerts();
    } catch (error) {
      console.error("Error toggling alert:", error);
      toast.error("Failed to update alert");
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await api.delete(`/alerts/${alertId}`);
      toast.success("Alert deleted");
      fetchAlerts();
    } catch (error) {
      console.error("Error deleting alert:", error);
      toast.error("Failed to delete alert");
    }
  };

  const handleCheckAlerts = async () => {
    try {
      await api.post("/alerts/check");
      toast.success("Alerts checked");
      fetchAlerts();
    } catch (error) {
      console.error("Error checking alerts:", error);
      toast.error("Failed to check alerts");
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="alerts-loading">
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 skeleton rounded-sm" />
          ))}
        </div>
      </div>
    );
  }

  const activeAlerts = alerts.filter(a => a.is_active && !a.triggered);
  const triggeredAlerts = alerts.filter(a => a.triggered);
  const inactiveAlerts = alerts.filter(a => !a.is_active && !a.triggered);

  return (
    <div className="space-y-6" data-testid="alerts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight">
            PRICE ALERTS
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Set up notifications for price movements
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCheckAlerts}
            className="gap-2"
            data-testid="check-alerts-btn"
          >
            <Zap className="w-4 h-4" />
            Check Now
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="create-alert-btn">
                <Plus className="w-4 h-4" />
                New Alert
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-heading">CREATE PRICE ALERT</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground mb-4">Get notified when price hits your target.</p>
              <form onSubmit={handleCreateAlert} className="space-y-4">
                <div className="space-y-2">
                  <Label>Asset</Label>
                  <Select
                    value={formData.symbol}
                    onValueChange={(value) => setFormData({ ...formData, symbol: value })}
                  >
                    <SelectTrigger data-testid="alert-symbol-select">
                      <SelectValue placeholder="Select asset" />
                    </SelectTrigger>
                    <SelectContent>
                      {SYMBOLS.map((s) => (
                        <SelectItem key={s.symbol} value={s.symbol}>
                          {s.name} ({s.symbol}) - ${prices[s.symbol]?.price?.toLocaleString() || "N/A"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Condition</Label>
                  <Select
                    value={formData.condition}
                    onValueChange={(value) => setFormData({ ...formData, condition: value })}
                  >
                    <SelectTrigger data-testid="alert-condition-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="above">Price goes ABOVE</SelectItem>
                      <SelectItem value="below">Price goes BELOW</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Target Price (USD)</Label>
                  <Input
                    type="number"
                    step="any"
                    placeholder="0.00"
                    value={formData.target_price}
                    onChange={(e) => setFormData({ ...formData, target_price: e.target.value })}
                    data-testid="alert-price-input"
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="submit-alert-btn">
                  Create Alert
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="card-dark">
          <CardContent className="p-4 text-center">
            <p className="text-muted-foreground text-xs">Active</p>
            <p className="font-data font-bold text-2xl text-profit">{activeAlerts.length}</p>
          </CardContent>
        </Card>
        <Card className="card-dark">
          <CardContent className="p-4 text-center">
            <p className="text-muted-foreground text-xs">Triggered</p>
            <p className="font-data font-bold text-2xl text-warning">{triggeredAlerts.length}</p>
          </CardContent>
        </Card>
        <Card className="card-dark">
          <CardContent className="p-4 text-center">
            <p className="text-muted-foreground text-xs">Inactive</p>
            <p className="font-data font-bold text-2xl text-muted-foreground">{inactiveAlerts.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Active Alerts */}
      <Card className="card-dark" data-testid="active-alerts-section">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Bell className="w-5 h-5 text-profit" />
            ACTIVE ALERTS
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {activeAlerts.length > 0 ? (
            activeAlerts.map((alert, index) => {
              const currentPrice = prices[alert.symbol]?.price || 0;
              const isAbove = alert.condition === "above";
              const progress = isAbove 
                ? (currentPrice / alert.target_price) * 100
                : (alert.target_price / currentPrice) * 100;

              return (
                <div
                  key={alert.id}
                  className="flex items-center justify-between p-4 bg-background border border-border animate-slide-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`alert-${alert.symbol}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 ${isAbove ? "bg-profit/20" : "bg-loss/20"} flex items-center justify-center`}>
                      {isAbove ? (
                        <TrendingUp className="w-5 h-5 text-profit" />
                      ) : (
                        <TrendingDown className="w-5 h-5 text-loss" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-heading font-bold">{alert.symbol}</p>
                        <Badge variant="outline" className="text-xs">
                          {alert.condition}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Current: <span className="font-data">${currentPrice?.toLocaleString()}</span>
                        {" → "}
                        Target: <span className="font-data text-white">${alert.target_price?.toLocaleString()}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${isAbove ? "bg-profit" : "bg-loss"}`}
                        style={{ width: `${Math.min(progress, 100)}%` }}
                      />
                    </div>
                    <Switch
                      checked={alert.is_active}
                      onCheckedChange={() => handleToggleAlert(alert.id)}
                      data-testid={`toggle-${alert.id}`}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteAlert(alert.id)}
                      className="text-muted-foreground hover:text-loss"
                      data-testid={`delete-${alert.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <Bell className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No active alerts</p>
              <p className="text-sm text-muted-foreground mt-1">Create an alert to get notified</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Triggered Alerts */}
      {triggeredAlerts.length > 0 && (
        <Card className="card-dark border-l-4 border-l-warning" data-testid="triggered-alerts-section">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Zap className="w-5 h-5 text-warning" />
              TRIGGERED ALERTS
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {triggeredAlerts.map((alert) => (
              <div
                key={alert.id}
                className="flex items-center justify-between p-4 bg-warning/10 border border-warning/30"
                data-testid={`triggered-${alert.symbol}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-warning/20 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-warning" />
                  </div>
                  <div>
                    <p className="font-heading font-bold">{alert.symbol}</p>
                    <p className="text-sm text-muted-foreground">
                      Target ${alert.target_price?.toLocaleString()} reached!
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteAlert(alert.id)}
                  className="text-muted-foreground hover:text-loss"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
